import styled from 'styled-components'
import { Box, Paper } from '@mui/material'

export const ModernSectionCard = styled(Paper)`
  padding: 24px;
  margin-bottom: 24px;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(103, 121, 227, 0.1);
  background: #ffffff;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;

  &:hover {
    box-shadow: 0 4px 16px rgba(103, 121, 227, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06);
  }
`

export const ModernSectionHeading = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 20px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid rgba(103, 121, 227, 0.2);
  letter-spacing: -0.02em;
  display: flex;
  align-items: center;
  gap: 8px;
`

export const ModernSectionSubheading = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #334155;
  margin: 0 0 16px 0;
  letter-spacing: -0.01em;
`

export const PatientDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-bottom: 8px;
`

export const PatientDetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const PatientDetailLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

export const PatientDetailValue = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: #1e293b;
`

export const FormFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
`

export const FormFieldLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
`

export const FormFieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
    gap: 20px;
  }
`

export const FormFieldLabelColumn = styled.div`
  min-width: 150px;
  max-width: 200px;
  flex-shrink: 0;
  padding-top: 8px;

  @media (max-width: 767px) {
    min-width: 100%;
    max-width: 100%;
    padding-top: 0;
  }
`

export const FormFieldInputColumn = styled.div`
  flex: 1;
  min-width: 0;
`

export const ModernTextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  font-family: inherit;
  color: #1e293b;
  background: #ffffff;
  transition: all 0.2s ease;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #6779e3;
    box-shadow: 0 0 0 3px rgba(103, 121, 227, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`

export const ModernCheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;
  background: rgba(103, 121, 227, 0.04);
  border: 2px solid rgba(103, 121, 227, 0.1);
  border-radius: 12px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(103, 121, 227, 0.08);
    border-color: rgba(103, 121, 227, 0.2);
  }
`

export const SectionDivider = styled.hr`
  border: none;
  height: 1px;
  background: linear-gradient(
    to right,
    transparent,
    rgba(103, 121, 227, 0.2),
    transparent
  );
  margin: 32px 0;
`

export const PageContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  background: #f8fafc;
  /* No height constraints - let content grow naturally for parent to scroll */

  @media (min-width: 768px) {
    padding: 32px;
  }

  /* Hide page number indicators - fixed position elements at bottom */
  div[style*="position: fixed"][style*="bottom"],
  div[style*="position:fixed"][style*="bottom"],
  div[style*="position: fixed"][style*="transform"][style*="translateX"],
  div[style*="position:fixed"][style*="transform"][style*="translateX"] {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
    height: 0 !important;
    width: 0 !important;
    overflow: hidden !important;
  }
`

export const StickyButtonsContainer = styled.div`
  position: sticky;
  top: 80px;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  padding: 16px 0;
  margin-bottom: 24px;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.8);
  isolation: isolate;
  transform: translateZ(0);
  will-change: transform;
`

// Quick Action Buttons for Preview and History Drawers
export const QuickActionsContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 16px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%);
  border-radius: 12px;
  border: 1px solid rgba(103, 121, 227, 0.15);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  flex-wrap: wrap;

  @media (max-width: 600px) {
    padding: 10px 12px;
    gap: 8px;
  }
`

export const QuickActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-transform: none;
  font-weight: 600;
  font-size: 13px;
  padding: 8px 16px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(103, 121, 227, 0.1) 0%, rgba(103, 121, 227, 0.05) 100%);
  color: #6779e3;
  border: 1px solid rgba(103, 121, 227, 0.2);
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;

  &:hover {
    background: linear-gradient(135deg, rgba(103, 121, 227, 0.2) 0%, rgba(103, 121, 227, 0.1) 100%);
    border-color: rgba(103, 121, 227, 0.4);
    box-shadow: 0 4px 12px rgba(103, 121, 227, 0.15);
  }

  svg {
    font-size: 18px;
  }

  @media (max-width: 600px) {
    padding: 6px 12px;
    font-size: 12px;
  }
`

// Drawer styled components
export const DrawerHeader = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(103, 121, 227, 0.15);
  background: linear-gradient(135deg, rgba(103, 121, 227, 0.08) 0%, rgba(103, 121, 227, 0.03) 100%);
  position: sticky;
  top: 0;
  z-index: 10;
  backdrop-filter: blur(10px);
`

export const DrawerContent = styled(Box)`
  padding: 20px;
  overflow-y: auto;
  height: calc(100% - 72px);
  background: #f8fafc;
`

